import { Building2, CheckCircle2, Languages, Quote, ShieldCheck, Sparkles, UsersRound } from "lucide-react";
import { CopyButton } from "../components/CopyButton";
import { LastUpdated } from "../components/LastUpdated";
import { PageHeader } from "../components/PageHeader";
import { PassportSection } from "../components/PassportSection";
import { SocialLinks } from "../components/SocialLinks";
import { StatusBadge } from "../components/StatusBadge";
import { WarningCallout } from "../components/WarningCallout";
import { projectPassport } from "../data/projectPassport";
import { formatDate } from "../lib/dates";

export function PassportPage() {
  const passport = projectPassport;

  return (
    <div className="page">
      <PageHeader
        eyebrow="Раздел 01"
        title="Паспорт проекта"
        description="Целостное и проверяемое представление Social by AKFA."
      />
      <LastUpdated date={passport.lastUpdatedAt} section />

      <div className="passport-hero">
        <div>
          <span className="eyebrow">Основной принцип</span>
          <blockquote>«{passport.principle}»</blockquote>
          <p>{passport.supportingPrinciple}</p>
        </div>
        <Quote size={48} strokeWidth={1.2} aria-hidden="true" />
      </div>

      <PassportSection id="name" eyebrow="01" title="Официальное название">
        <div className="info-card name-card">
          <div className="name-card__brand">
            <img src="/assets/social-logo-white.svg" alt="Social" />
          </div>
          <div className="name-card__details">
            <dl className="details-list">
              <div><dt>Русский</dt><dd>{passport.names.ru}</dd></div>
              <div><dt>O‘zbekcha</dt><dd>{passport.names.uz}</dd></div>
              <div><dt>English</dt><dd>{passport.names.en}</dd></div>
              <div><dt>Допустимое упоминание</dt><dd>{passport.names.allowedMention}</dd></div>
            </dl>
            <CopyButton text={passport.names.official} label="Скопировать название" />
          </div>
        </div>
        <div className="avoid-list">
          <strong>Недопустимые варианты</strong>
          <div>{passport.names.prohibitedVariants.map((item) => <span key={item}>{item}</span>)}</div>
        </div>
      </PassportSection>

      <PassportSection id="description" eyebrow="02" title="Описание проекта">
        <div className="copy-cards">
          <article className="copy-card">
            <span className="copy-card__label">Краткое описание</span>
            <p>{passport.shortDescription}</p>
            <CopyButton text={passport.shortDescription} />
          </article>
          <article className="copy-card">
            <span className="copy-card__label">Расширенное описание</span>
            <p>{passport.fullDescription}</p>
            <WarningCallout>{passport.fullDescriptionNote}</WarningCallout>
            <CopyButton text={passport.fullDescription} />
          </article>
        </div>
      </PassportSection>

      <PassportSection id="mission" eyebrow="03" title="Миссия и принципы">
        <div className="mission-grid">
          <article className="mission-card mission-card--primary">
            <Sparkles size={24} aria-hidden="true" />
            <span>Миссия</span>
            <p>{passport.mission}</p>
          </article>
          <article className="mission-card">
            <ShieldCheck size={24} aria-hidden="true" />
            <span>Основной принцип</span>
            <p>{passport.principle}</p>
            <small>{passport.supportingPrinciple}</small>
          </article>
        </div>
      </PassportSection>

      <PassportSection eyebrow="04" title="Тип проекта">
        <div className="tag-cloud">
          {passport.projectTypes.map((item) => <span key={item}>{item}</span>)}
        </div>
      </PassportSection>

      <PassportSection id="directions" eyebrow="05" title="Основные направления">
        <div className="content-grid">
          {passport.directions.map((item, index) => (
            <article className="content-card" key={item.title}>
              <span className="content-card__index">{String(index + 1).padStart(2, "0")}</span>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </article>
          ))}
        </div>
      </PassportSection>

      <PassportSection id="audience" eyebrow="06" title="Целевая аудитория">
        <div className="audience-grid">
          {passport.audiences.map((item) => (
            <article className="audience-card" key={item.title}>
              <UsersRound size={20} aria-hidden="true" />
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </article>
          ))}
        </div>
      </PassportSection>

      <PassportSection eyebrow="07" title="Ключевые особенности">
        <div className="feature-list">
          {passport.features.map((item) => (
            <div key={item}><CheckCircle2 size={18} aria-hidden="true" /><span>{item}</span></div>
          ))}
        </div>
      </PassportSection>

      <PassportSection eyebrow="08" title="Связанные организации">
        <div className="organization-grid">
          {passport.organizations.map((item) => (
            <article className="organization-card" key={item.title}>
              <Building2 size={22} aria-hidden="true" />
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              <StatusBadge status={item.verification} />
            </article>
          ))}
        </div>
      </PassportSection>

      <PassportSection eyebrow="09" title="Текущий статус">
        <article className="status-card">
          <div className="status-card__top">
            <div><span>Статус проекта</span><strong>{passport.currentStatus.status}</strong></div>
            <StatusBadge status="needs-review" />
          </div>
          <dl className="details-list">
            <div><dt>Этап</dt><dd>{passport.currentStatus.stage}</dd></div>
            <div><dt>Обновлено</dt><dd>{formatDate(passport.currentStatus.updatedAt)}</dd></div>
          </dl>
          <WarningCallout title="Сроки запуска">{passport.currentStatus.note}</WarningCallout>
        </article>
      </PassportSection>

      <PassportSection
        id="channels"
        eyebrow="10"
        title="Официальные каналы"
        description="Проверенные страницы Social by AKFA и официальный сайт проекта."
      >
        <SocialLinks />
      </PassportSection>

      <div className="language-note"><Languages size={18} /> Структура данных готова для независимых русской, узбекской и английской версий.</div>
    </div>
  );
}
